import { Plus, Zap, Play, Pause, Activity, ListChecks, Send } from 'lucide-react';
import { useState } from 'react';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';

import { ProFeatureLock } from '@/features/billing/ui/pro-feature-lock';
import { useAutomationRules, useUpdateRule } from '@/features/automation';
import { testIds } from '@/shared/testing';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { DataTablePagination } from '@/shared/ui/data-table-pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { PageHeader } from '@/shared/ui/page-header';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { AnimatedContainer } from '@/shared/ui/animated';
import { AppLayout } from '../-components/app-layout';

import type { AutomationTriggerType } from '@cafe24-apps/traffic-light-server/contracts';

export const Route = createFileRoute('/_authenticated/$mallId/automation/')({
  component: AutomationListPage,
});

const TRIGGER_TYPE_LABELS: Record<AutomationTriggerType, string> = {
  segment_change: '고객 상태 이동',
  ltv_cohort_change: 'LTV 등급 이동',
  overdue_entry: '구매주기 초과',
};

function AutomationListPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/automation/' });
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [triggerFilter, setTriggerFilter] = useState<AutomationTriggerType | 'all'>('all');

  const { data, isLoading } = useAutomationRules({
    page,
    limit,
    triggerType: triggerFilter === 'all' ? undefined : triggerFilter,
  });

  // 전체 활성 규칙 수 (페이지 무관)
  const { data: activeData } = useAutomationRules({ limit: 1, enabled: true });
  // 전체 발급 수 집계용 (limit 999로 전체 조회)
  const { data: allData } = useAutomationRules({ limit: 999 });

  const { mutate: updateRule } = useUpdateRule();

  const rules = data?.data ?? [];
  const pagination = data?.pagination;

  const activeCount = activeData?.pagination?.total ?? 0;
  const totalIssued = (allData?.data ?? []).reduce((sum, r) => sum + r.totalIssued, 0);

  return (
    <AppLayout pageTitle="자동화">
      <ProFeatureLock feature="couponAutoIssueEnabled">
        <div className="space-y-6 max-w-5xl mx-auto">
          <AnimatedContainer delay={0}>
            <PageHeader
              title="자동화 규칙"
              description="분석 결과 기반으로 쿠폰을 자동 발급합니다"
              action={
                <Button asChild className="gap-1.5">
                  <Link
                    to="/$mallId/automation/new"
                    params={{ mallId: mallId }}
                    data-testid={testIds.automation.newRuleButton}
                  >
                    <Plus className="w-4 h-4" />새 규칙
                  </Link>
                </Button>
              }
            />
          </AnimatedContainer>

          <AnimatedContainer delay={0.1}>
            <AnalysisGuide title="자동화는 이렇게 동작해요">
              <p>
                고객의 상태가 바뀌면 자동으로 쿠폰을 발급해요. 예를 들어{' '}
                <strong>활성 → 주의</strong>로 떨어진 고객에게 리텐션 쿠폰을 보낼 수 있어요.
              </p>
              <p>
                규칙을 만들어 두면 매번 수동으로 발급하지 않아도, 조건에 맞는 고객에게 알아서 쿠폰이
                나가요.
              </p>
              <div className="mt-3 pt-3 border-t border-border/50">
                <p className="font-medium text-foreground/80">이렇게 활용해 보세요</p>
                <ul className="mt-1.5 space-y-1 text-muted-foreground list-disc list-inside">
                  <li>
                    <strong>고객 상태 이동</strong> — 활성에서 주의/이탈로 떨어진 고객에게 리텐션
                    쿠폰을 보내보세요.
                  </li>
                  <li>
                    <strong>LTV 등급 이동</strong> — 성장 고객이 이탈 위험으로 바뀌면 즉시 대응할 수
                    있어요.
                  </li>
                  <li>
                    <strong>구매주기 초과</strong> — 평소 사던 주기를 넘긴 고객에게 리마인더 쿠폰이
                    효과적이에요.
                  </li>
                </ul>
              </div>
            </AnalysisGuide>
          </AnimatedContainer>

          <AnimatedContainer delay={0.2}>
            <div className="grid grid-cols-3 gap-4">
              <Card className="py-4" data-testid={testIds.automation.totalRulesCard}>
                <CardContent className="flex items-center gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <ListChecks className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">전체 규칙</p>
                    <p className="text-2xl font-semibold tabular-nums">{pagination?.total ?? 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="py-4" data-testid={testIds.automation.activeRulesCard}>
                <CardContent className="flex items-center gap-3">
                  <div className="rounded-lg bg-signal-green/10 p-2">
                    <Activity className="h-4 w-4 text-signal-green" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">활성 규칙</p>
                    <p className="text-2xl font-semibold tabular-nums text-signal-green-text">
                      {activeCount}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="py-4" data-testid={testIds.automation.totalIssuedCard}>
                <CardContent className="flex items-center gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <Send className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">총 발급 수</p>
                    <p className="text-2xl font-semibold tabular-nums">
                      {totalIssued.toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </AnimatedContainer>

          <AnimatedContainer delay={0.3}>
            <Card className="py-0 gap-0">
              <div className="flex items-center gap-3 px-6 py-4 border-b">
                <Select
                  value={triggerFilter}
                  onValueChange={(v) => {
                    setTriggerFilter(v as AutomationTriggerType | 'all');
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-48" data-testid={testIds.automation.triggerFilter}>
                    <SelectValue placeholder="트리거 필터" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">전체</SelectItem>
                    <SelectItem value="segment_change">고객 상태 이동</SelectItem>
                    <SelectItem value="ltv_cohort_change">LTV 등급 이동</SelectItem>
                    <SelectItem value="overdue_entry">구매주기 초과</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Table data-testid={testIds.automation.rulesTable}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">상태</TableHead>
                    <TableHead>이름</TableHead>
                    <TableHead>트리거</TableHead>
                    <TableHead>쿠폰</TableHead>
                    <TableHead className="text-right">총 발급</TableHead>
                    <TableHead>최근 실행</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Skeleton className="h-5 w-10" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-40" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-24" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-32" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-16" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-24" />
                          </TableCell>
                        </TableRow>
                      ))
                    : rules.map((rule) => (
                        <TableRow
                          key={rule.id}
                          className="cursor-pointer hover:bg-accent/50"
                          data-testid={testIds.automation.ruleRow}
                          onClick={() =>
                            navigate({
                              to: '/$mallId/automation/$ruleId',
                              params: { mallId: mallId, ruleId: rule.id },
                            })
                          }
                        >
                          <TableCell>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateRule({ ruleId: rule.id, enabled: !rule.enabled });
                              }}
                              className="p-1 rounded-lg hover:bg-accent transition-colors"
                              data-testid={testIds.automation.statusToggle}
                            >
                              {rule.enabled ? (
                                <Play className="w-4 h-4 text-signal-green fill-signal-green" />
                              ) : (
                                <Pause className="w-4 h-4 text-muted-foreground" />
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="font-medium">{rule.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{TRIGGER_TYPE_LABELS[rule.triggerType]}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {rule.cafe24CouponName}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {rule.totalIssued.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm tabular-nums">
                            {rule.lastRunAt
                              ? new Date(rule.lastRunAt).toLocaleDateString('ko-KR')
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                  {!isLoading && rules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div
                          className="flex flex-col items-center gap-3"
                          data-testid={testIds.automation.emptyState}
                        >
                          <div className="rounded-full bg-muted p-3">
                            <Zap className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">아직 자동화 규칙이 없어요</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              첫 규칙을 만들면 조건에 맞는 고객에게 쿠폰이 자동으로 발급돼요
                            </p>
                          </div>
                          <Button asChild size="sm" className="gap-1.5 mt-1">
                            <Link
                              to="/$mallId/automation/new"
                              params={{ mallId: mallId }}
                              data-testid={testIds.automation.createFirstButton}
                            >
                              <Plus className="w-4 h-4" />첫 규칙 만들기
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {pagination && pagination.total > 0 && (
                <DataTablePagination
                  page={page}
                  limit={limit}
                  total={pagination.total}
                  totalPages={Math.ceil(pagination.total / limit)}
                  onPageChange={setPage}
                  onLimitChange={(l) => {
                    setLimit(l);
                    setPage(1);
                  }}
                />
              )}
            </Card>
          </AnimatedContainer>
        </div>
      </ProFeatureLock>
    </AppLayout>
  );
}
